import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { AlertController } from '@ionic/angular';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { TranslateService } from '@ngx-translate/core';

export type PermKind = 'camera' | 'photos' | 'location';

/**
 * Central permission handling.
 *
 * Two entry points:
 *   ensureFirstRun()  — asks for camera / photos / location ONCE on first launch
 *   ensure(kind)      — call right before using a feature. If the permission is
 *                       still available to ask for, it asks; if the user has
 *                       permanently denied it, Android will NOT show the system
 *                       dialog again, so we explain and offer Settings instead.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private static readonly FIRST_RUN_KEY = 'permissionsPrimed';

  constructor(
    private alertCtrl: AlertController,
    private translate: TranslateService,
  ) {}

  private get native(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** Ask for everything once, the first time the app is opened. */
  async ensureFirstRun(): Promise<void> {
    if (!this.native) return;
    if (localStorage.getItem(PermissionService.FIRST_RUN_KEY) === 'yes') return;

    // Mark first so a crash/abort mid-prompt doesn't re-nag on every launch;
    // ensure() still re-asks lazily when a feature is actually used.
    localStorage.setItem(PermissionService.FIRST_RUN_KEY, 'yes');

    try { await Camera.requestPermissions({ permissions: ['camera', 'photos'] }); } catch { }
    try { await Geolocation.requestPermissions(); } catch { }
  }

  /**
   * Guarantee a permission before using a feature.
   * @returns true if granted, false if the caller should abort.
   */
  async ensure(kind: PermKind): Promise<boolean> {
    if (!this.native) return true;          // browser: the API prompts itself

    let state = await this.check(kind);
    if (state === 'granted') return true;

    // 'prompt' / 'prompt-with-rationale' -> the system dialog can still appear
    if (state !== 'denied') {
      state = await this.request(kind);
      if (state === 'granted') return true;
    }

    // Permanently denied: Android will not show the dialog again, so the only
    // route left is the app's Settings page.
    await this.openSettingsPrompt(kind);
    return false;
  }

  /** set when the last location check failed because GPS itself is switched off */
  private locationServicesOff = false;

  private async check(kind: PermKind): Promise<string> {
    try {
      if (kind === 'location') {
        this.locationServicesOff = false;
        return (await Geolocation.checkPermissions()).location;
      }
      const p = await Camera.checkPermissions();
      return kind === 'camera' ? p.camera : p.photos;
    } catch (e: any) {
      // "Location services are not enabled" is the OS-level GPS master switch
      // being off — NOT the user denying this app. Sending them to the app's
      // permission page would be a dead end, so flag it and route them to the
      // Location settings screen instead.
      if (kind === 'location' && /location services/i.test(String(e?.message ?? e))) {
        this.locationServicesOff = true;
      }
      return 'denied';
    }
  }

  private async request(kind: PermKind): Promise<string> {
    try {
      if (kind === 'location') return (await Geolocation.requestPermissions()).location;
      const p = await Camera.requestPermissions({
        permissions: [kind === 'camera' ? 'camera' : 'photos'],
      });
      return kind === 'camera' ? p.camera : p.photos;
    } catch { return 'denied'; }
  }

  /** Explain what is blocked and offer to jump to the app's settings page. */
  private async openSettingsPrompt(kind: PermKind): Promise<void> {
    const t = (k: string) => this.translate.instant(k);
    const gpsOff = kind === 'location' && this.locationServicesOff;
    const alert = await this.alertCtrl.create({
      header: t('PERMISSION.TITLE'),
      message: t(gpsOff ? 'PERMISSION.LOCATION_OFF' : `PERMISSION.${kind.toUpperCase()}_DENIED`),
      backdropDismiss: true,
      buttons: [
        { text: t('PERMISSION.NOT_NOW'), role: 'cancel' },
        {
          text: t('PERMISSION.OPEN_SETTINGS'),
          handler: () => { gpsOff ? this.openLocationSettings() : this.openAppSettings(); },
        },
      ],
    });
    await alert.present();
  }

  /** The OS Location screen — for when GPS itself is switched off. */
  private async openLocationSettings(): Promise<void> {
    try {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.Location,
        optionIOS: IOSSettings.LocationServices,
      });
    } catch (e) {
      console.error('Could not open location settings', e);
    }
  }

  /** Deep-link straight to this app's own permission screen. */
  private async openAppSettings(): Promise<void> {
    try {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
    } catch (e) {
      console.error('Could not open app settings', e);
    }
  }
}
